'use client'

import { motion, AnimatePresence } from 'framer-motion'
import {
  FileText,
  Receipt,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  X,
  CheckCircle,
  Download,
  Upload,
  LogOut,
  Sparkles,
  ArrowLeft,
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { toast } from 'react-toastify'
import { useClient } from '../../contexts/ClientContext'

const baseMenuItems = [
  { name: 'Profile', icon: FileText, pageKey: 'profile', section: 'profile' },
  { name: 'Challan Details', icon: Receipt, pageKey: 'challan-details', section: 'challan-details' },
  { name: 'TDS Details', icon: FileText, pageKey: 'tds-details', section: 'tds-details' }
]

const TdsSidebar = ({ isOpen, isMobile, onClose, activeSection, onSectionChange, onSidebarStateChange, tdsData, challanData, showSalaryDetails, show194P, selectionComplete = true }) => {

  const menuItems = [...baseMenuItems]
  if (showSalaryDetails) {
    menuItems.push({ name: 'Salary Details', icon: FileText, pageKey: 'salary-details', section: 'salary-details' })
  }
  if (show194P) {
    menuItems.push({ name: 'Section 194P', icon: Receipt, pageKey: 'senior-citizens-194p', section: 'senior-citizens-194p' })
  }
  const navigate = useNavigate()
  const location = useLocation()
  const { activeClient } = useClient()
  const [expandedItem, setExpandedItem] = useState('Form Sections')
  const [hovered, setHovered] = useState(false)
  const [keepOpen, setKeepOpen] = useState(() => {
    const stored = localStorage.getItem('tdsSidebarKeepOpen')
    return stored ? stored === 'true' : true
  })

  // Store the callback in a ref to prevent infinite loops
  const callbackRef = useRef(onSidebarStateChange)

  // Update the ref when the callback changes
  useEffect(() => {
    callbackRef.current = onSidebarStateChange
  }, [onSidebarStateChange])

  useEffect(() => {
    localStorage.setItem('tdsSidebarKeepOpen', keepOpen)
  }, [keepOpen])

  // Notify parent component of sidebar state changes
  useEffect(() => {
    if (callbackRef.current) {
      const isExpanded = isMobile ? isOpen : (keepOpen || hovered)
      const sidebarOpenState = isMobile ? isOpen : true
      callbackRef.current({
        isOpen: sidebarOpenState,
        isExpanded: isExpanded,
        keepOpen: keepOpen,
        hovered: hovered
      })
    }
  }, [keepOpen, hovered, isMobile, isOpen])

  // Auto-expand parent if URL matches a subItem
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.subItems) {
        const match = item.subItems.find((sub) => sub.link === location.pathname)
        if (match) {
          setExpandedItem(item.name)
        }
      }
    })
  }, [location.pathname])

  // Listen for global AI data extraction
  useEffect(() => {
    const handleAiData = (e) => handleAiExtractedData(e.detail);
    window.addEventListener('aiDataExtracted', handleAiData);
    return () => window.removeEventListener('aiDataExtracted', handleAiData);
  }, []);

  const isActive = (item) => {
    if (item.link && location.pathname === item.link) return true
    if (item.subItems) {
      return item.subItems.some((sub) => sub.link === location.pathname)
    }
    return false
  }

  const isSectionActive = (section) => {
    return activeSection === section
  }

  // Sidebar open state logic
  let sidebarOpen
  if (isMobile) {
    sidebarOpen = isOpen
  } else {
    // On desktop, always show sidebar (either collapsed or expanded)
    sidebarOpen = true
  }

  // Sidebar width for collapsed/expanded
  const collapsedWidth = '3.5rem'
  const expandedWidth = '16rem'

  // Determine if sidebar should be expanded (not just visible)
  const isExpanded = isMobile ? isOpen : (keepOpen || hovered)

  const handleItemClick = (item) => {
    if (item.subItems) {
      setExpandedItem(expandedItem === item.name ? '' : item.name)
    } else if (item.link) {
      navigate(item.link)
      if (isMobile) onClose()
    } else if (item.section) {
      onSectionChange(item.section)
      if (isMobile) onClose()
    }
  }

  const handleSubItemClick = (subItem) => {
    if (subItem.link) {
      navigate(subItem.link)
    } else if (subItem.section) {
      onSectionChange(subItem.section)
    }
    if (isMobile) onClose()
  }

  const fileInputRef = useRef(null)

  // Parse Excel Date helper
  const parseExcelDate = (dateValue) => {
    if (!dateValue) return ''

    // Handle Excel Serial Numbers using lib
    if (typeof dateValue === 'number') {
      const dateObj = XLSX.SSF.parse_date_code(dateValue);
      if (dateObj) {
        const year = dateObj.y;
        const month = String(dateObj.m).padStart(2, '0');
        const day = String(dateObj.d).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    // Handle Strings (DD/MM/YYYY or DD-MM-YYYY or YYYY-MM-DD)
    if (typeof dateValue === 'string') {
      const cleanDate = dateValue.trim().replace(/-/g, '/');
      if (cleanDate.includes('/')) {
        const parts = cleanDate.split('/');
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
          }
          return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
    }

    // Fallback for Date objects (if passed somehow)
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear()
      const month = String(dateValue.getMonth() + 1).padStart(2, '0')
      const day = String(dateValue.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return dateValue
  }

  // Section Codes List (Shared for Validation and Template)
  // Note: These codes must match the dropdown options in TdsDetailsTable
  const validSectionCodes = [
    '192A', '193', '194', '194A', '194B', '194B-P', '194BA', '194BA-P', '194BB', '194C',
    '194D', '194DA', '194EE', '194F', '194G', '194H', '194I', '194I(a)', '194I(b)',
    '194IA', '194IC', '194J(a)', '194J(b)', '194K', '194LA',
    '194LBA', '194LBA(a)', '194LBA(b)', '194LBB', '194LBC', '194N',
    '194N-FT', '194NC', '194NF', '194O', '194Q', '194R', '194RP', '194S',
    '194SP',
    '194IB',
    '194M',
    // 27EQ Collection Codes
    '206C(1)', '206C(1C)', '206C(1F)', '206C(1G)', '206C(1H)', '206C(1I)', '206C(1J)',
    // 27Q Section Codes
    '194E', '195', '196A', '196B', '196C', '196D', '194LB', '194LC', '194LC(2)(i)', '194LC(2)(ib)', '194LC(2)(ic)', '194LD', '196D(1A)', '194T',
    // 24Q Section Codes
    '92A', '92B', '92C', '94P',
    // New Form Codes (138, 140, 144 etc)
    '1001', '1002', '1003', '1004', '1005', '1006', '1007', '1008', '1009', '1010',
    '1011', '1012', '1013', '1014', '1015', '1017', '1018', '1019', '1020', '1021',
    '1022', '1023', '1024', '1026', '1027', '1028', '1029', '1030', '1031', '1032',
    '1033', '1034', '1035', '1037', '1038', '1039', '1040', '1041', '1042', '1043',
    '1044', '1045', '1046', '1047', '1048', '1049', '1050', '1051', '1052', '1053',
    '1054', '1055', '1056', '1057', '1058', '1059', '1060', '1061', '1062', '1063',
    '1064', '1065', '1066', '1067', '1068', '1069', '1070', '1071', '1072', '1073',
    '1074', '1075', '1076', '1077', '1078', '1079', '1080', '1081', '1082', '1083',
    '1084', '1085', '1086', '1087', '1088', '1089', '1090', '1091', '1092', '1093',
    '1094', '1095', '1096', '1097', '1098', '1099', '1100'
  ];

  const getFieldValue = (row, possibleKeys) => {
    if (!row) return '';
    let val = '';
    for (const key of possibleKeys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
        val = row[key];
        break;
      }
    }
    if (val === '' || val === undefined || val === null) {
      const rowKeys = Object.keys(row);
      for (const targetKey of possibleKeys) {
        const cleanTarget = targetKey.toLowerCase().replace(/[*_.\s-]/g, '');
        for (const rKey of rowKeys) {
          const cleanRKey = rKey.toLowerCase().replace(/[*_.\s-]/g, '');
          if (cleanRKey === cleanTarget || (cleanTarget.length >= 3 && cleanRKey.includes(cleanTarget))) {
            if (row[rKey] !== undefined && row[rKey] !== null && row[rKey] !== '') {
              val = row[rKey];
              break;
            }
          }
        }
        if (val !== '' && val !== undefined && val !== null) break;
      }
    }
    if (typeof val === 'string') {
      val = val.replace(/[\r\n]/g, ' ').replace(/\s+/g, ' ').trim();
    }
    return val || '';
  };

  const handleExcelImport = (e) => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result)
        const workbook = XLSX.read(data, { type: 'array', cellDates: false })
        const sheetNames = workbook.SheetNames

        // Identify sheets intelligently by name or inspection
        let wsChallan = null
        let wsDeductee = null
        let wsSalary = workbook.Sheets["Salary Details"]

        // 1. Direct name match
        if (workbook.Sheets["Challan Details"]) wsChallan = workbook.Sheets["Challan Details"]
        if (workbook.Sheets["Deductee Details"]) wsDeductee = workbook.Sheets["Deductee Details"]
        if (workbook.Sheets["Collectee Details"]) wsDeductee = workbook.Sheets["Collectee Details"]

        // 2. Search sheets by name
        sheetNames.forEach(sName => {
          const sheet = workbook.Sheets[sName]
          if (!sheet) return
          const lower = sName.toLowerCase()
          if (!wsChallan && (lower.includes('challan') || lower.includes('cd'))) {
            wsChallan = sheet
          } else if (!wsDeductee && (lower.includes('deductee') || lower.includes('collectee') || lower.includes('dd') || lower.includes('tds'))) {
            wsDeductee = sheet
          }
        })

        // 3. Inspect headers of sheets if still missing
        sheetNames.forEach(sName => {
          const sheet = workbook.Sheets[sName]
          if (!sheet) return
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 })
          if (rows.length === 0) return
          const headerRow = (rows[0] || []).map(h => String(h || '').toLowerCase().trim())
          const hasChallanCols = headerRow.some(h => h.includes('bsr') || h.includes('date of deposit') || h.includes('challan serial'))
          const hasDeducteeCols = headerRow.some(h => h.includes('pan') || h.includes('deductee') || h.includes('amount') || h.includes('payment') || h.includes('first name'))

          if (!wsChallan && hasChallanCols && !hasDeducteeCols) {
            wsChallan = sheet
          }
          if (!wsDeductee && hasDeducteeCols) {
            wsDeductee = sheet
          }
        })

        // If only 1 sheet in workbook and neither matched
        if (!wsChallan && !wsDeductee && sheetNames.length === 1) {
          const firstSheet = workbook.Sheets[sheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })
          const headerRow = (rows[0] || []).map(h => String(h || '').toLowerCase().trim())
          const hasDeducteeCols = headerRow.some(h => h.includes('pan') || h.includes('deductee') || h.includes('amount') || h.includes('payment') || h.includes('section'))
          if (hasDeducteeCols) {
            wsDeductee = firstSheet
          } else {
            wsChallan = firstSheet
          }
        }

        let newChallans = []
        if (wsChallan) {
          const challanRows = XLSX.utils.sheet_to_json(wsChallan, { defval: '' })
          // Check if rows actually have challan data
          const validChallanRows = challanRows.filter(row => {
            const bsr = getFieldValue(row, ["BSR Code", "BSR", "BSRCode"])
            const cNo = getFieldValue(row, ["Challan Challan No", "Challan No", "Challan Serial No", "Challan Number"])
            const tot = getFieldValue(row, ["Total Amount", "Challan Amount", "Total Amount (Rs)", "Amount"])
            return bsr || cNo || tot
          })

          if (validChallanRows.length > 0) {
            newChallans = validChallanRows.map((row, idx) => {
              let bsr = String(getFieldValue(row, ["BSR Code", "BSR", "BSRCode"]) || '').trim()
              if (bsr && /^\d+$/.test(bsr) && bsr.length < 7) {
                bsr = bsr.padStart(7, '0')
              }
              let cNo = String(getFieldValue(row, ["Challan Challan No", "Challan No", "Challan Serial No", "Challan Number"]) || '').trim()
              if (cNo && /^\d+$/.test(cNo) && cNo.length < 5) {
                cNo = cNo.padStart(5, '0')
              }

              const intAlloc = getFieldValue(row, ["Interest Allocation", "InterestAlloc", "Interest Allocation (Rs)", "Int Allocation", "Interest_Allocation"])
              const feesAlloc = getFieldValue(row, ["Fees Allocation", "Fee Allocation", "Fees Allocation (Rs)", "Other Allocation", "Fee Alloc", "Fees_Allocation"])

              return {
                id: Date.now() + Math.random(),
                _serial: getFieldValue(row, ["Challan Serial No.", "Serial No.", "Challan Serial No", "Sl No", "Ref", "Challan No", "Serial"]) || (idx + 1),
                bsrCode: bsr,
                dateOfDeposit: parseExcelDate(getFieldValue(row, ["Date of Deposit (DD/MM/YYYY)", "Date of Deposit", "Deposit Date", "Date"])),
                totalAmount: getFieldValue(row, ["Total Amount", "Challan Amount", "Total Amount (Rs)", "Amount"]),
                interestAllocation: intAlloc !== undefined && intAlloc !== '' ? parseFloat(intAlloc) || 0 : (parseFloat(getFieldValue(row, ["Interest"])) || 0),
                feesAllocation: feesAlloc !== undefined && feesAlloc !== '' ? parseFloat(feesAlloc) || 0 : (parseFloat(getFieldValue(row, ["Fee", "Fees", "Penalty", "Other"])) || 0),
                otherAllocation: feesAlloc !== undefined && feesAlloc !== '' ? parseFloat(feesAlloc) || 0 : (parseFloat(getFieldValue(row, ["Fee", "Fees", "Penalty", "Other"])) || 0),
                challanNo: cNo,
                challanSerialNo: cNo,
                sectionCode: String(getFieldValue(row, ["Section Code", "Section", "SectionCode"]) || '').trim(),
                minorHead: getFieldValue(row, ["Minor Head", "MinorHead", "Head"]),
                tds: getFieldValue(row, ["TDS", "TDS Amount", "TDS Amt", "Tax Amount"]),
                tdsAmount: getFieldValue(row, ["TDS", "TDS Amount", "TDS Amt", "Tax Amount"]),
                surcharge: getFieldValue(row, ["Surcharge"]) || 0,
                educationCess: getFieldValue(row, ["Education Cess"]) || 0,
                interest: getFieldValue(row, ["Interest"]) || 0,
                fee: getFieldValue(row, ["Fee"]) || 0,
                penalty: getFieldValue(row, ["Penalty"]) || 0,
                other: getFieldValue(row, ["Other"]) || 0,
                higherEducationCess: getFieldValue(row, ["Higher Education Cess"]) || 0
              }
            })
          }
        }

        // Parse Deductees
        let newDeductees = []
        if (wsDeductee) {
          const deducteeRows = XLSX.utils.sheet_to_json(wsDeductee, { defval: '' })
          const availableChallans = (newChallans && newChallans.length > 0) ? newChallans : (challanData || [])

          newDeductees = deducteeRows.map(row => {
            const ref = getFieldValue(row, ["Challan Serial No. (Ref)", "Challan Serial No", "Challan Ref", "Challan", "Ref No.", "Ref", "Challan Serial No."])
            const parentChallan = availableChallans.find((c, i) => 
              String(c._serial) === String(ref) || 
              String(c.challanSerialNo) === String(ref) || 
              String(c.recordNo) === String(ref) || 
              String(i + 1) === String(ref)
            ) || (availableChallans.length > 0 ? availableChallans[0] : null)

            const amountOfPayment = getFieldValue(row, ["Amount Paid", "Amount of Payment", "Payment Amt", "Payment Amount", "Amount Paid / Credited", "Amount Paid/Credited", "Amount", "Total Value of Purchase", "Collected Amt", "Payment Amt *"])
            let tdsAmount = getFieldValue(row, ["TDS Amount", "TDS Amt", "TDS", "TCS Amount", "TCS Amt", "Tax Deducted", "Total Tax Deducted", "Income Tax", "TDS Amount (Rs)", "TDS Amt *", "TDS Amount *", "Tax Amount"])
            const rateOfTax = getFieldValue(row, ["Rate at which Tax Deducted", "Rate at which deducted", "Rate %", "Rate", "Rate of Tax", "Tax Rate", "Rate % *"])

            const paymentNum = parseFloat(amountOfPayment || 0)
            const rateNum = parseFloat(rateOfTax || 0)

            // Auto-calculate TDS Amount if missing/empty but Payment Amount & Rate % are present
            if ((tdsAmount === '' || tdsAmount === undefined || tdsAmount === null || isNaN(parseFloat(tdsAmount))) && paymentNum > 0 && rateNum > 0) {
              tdsAmount = Math.round(((paymentNum * rateNum) / 100) * 100) / 100
            }

            const dateOfPayment = parseExcelDate(getFieldValue(row, ["Date of Payment (DD/MM/YYYY)", "Date of Payment", "Date Payment", "Date of Collection (DD/MM/YYYY)", "Date Payment *"]))
            const dateOfDeduction = parseExcelDate(getFieldValue(row, ["Date of Deduction (DD/MM/YYYY)", "Date of Deduction", "Date Ded.", "Date Ded. *", "Date of Collection"]))

            const firstName = getFieldValue(row, ["First Name", "FirstName", "First Na"]) || ""
            const middleName = getFieldValue(row, ["Middle Name", "MiddleName", "Middle"]) || ""
            const lastName = getFieldValue(row, ["Last Name", "LastName", "Last Na", "Last"]) || ""
            const rawName = getFieldValue(row, ["Deductee Name", "Collectee Name", "Name", "Name of Deductee", "Party Name"]) || ""
            const fullName = rawName || [firstName, middleName, lastName].filter(Boolean).join(" ").trim()

            const recordNoRaw = getFieldValue(row, ["Deductee Record No", "Deductee Record No.", "Record No", "Record No.", "Serial No", "Serial No.", "Sr No", "Sr. No.", "S.No", "S.No."])
            const recordNoVal = recordNoRaw && parseInt(recordNoRaw, 10) > 0 ? parseInt(recordNoRaw, 10) : undefined
            const secCode = String(getFieldValue(row, ["Section Code", "Section", "Section *", "Collection Code", "SectionCode"]) || '').trim()

            return {
              id: Date.now() + Math.random(),
              recordNo: recordNoVal,
              challanId: parentChallan ? parentChallan.id : (availableChallans.length > 0 ? availableChallans[0].id : ""),
              deducteeCode: getFieldValue(row, ["Deductee Code", "Collectee Code", "Deductee", "Collectee"]) || "2",
              panOfDeductee: getFieldValue(row, ["PAN of Deductee", "PAN", "PAN of Collectee", "Deductee PAN", "PAN *"]),
              firstName: firstName,
              middleName: middleName,
              lastName: lastName,
              deducteeName: fullName,
              sectionCode: secCode,
              amountOfPayment: amountOfPayment,
              tdsAmount: tdsAmount !== '' && tdsAmount !== undefined && tdsAmount !== null ? tdsAmount : '',
              surcharge: getFieldValue(row, ["Surcharge"]) || 0,
              educationCess: getFieldValue(row, ["Education Cess", "Cess"]) || 0,
              dateOfPayment: dateOfPayment,
              dateOfDeduction: dateOfDeduction || '',
              rateOfTax: rateOfTax,
              reasonForNonDeduction: getFieldValue(row, ["Reason for non-deduction", "Reason for Non-Deduction", "Reason Non-Ded", "Remarks"]),
              certificateNo197: getFieldValue(row, ["Certificate No. 197", "Certificate No 197", "Certificate No.", "Cert. 197", "Certificate"]) || "",
              // 27Q / 144 / 27EQ / 143 Fields
              natureOfRemittance: getFieldValue(row, ["Nature of Remittance", "Remittance", "Nature of Remittance / Payment"]) || "",
              country: getFieldValue(row, ["Country", "Country of Residence", "Country Name"]) || "",
              applicableTds: getFieldValue(row, ["Applicable TDS Section", "Appl. TDS", "Applicable TDS"]) || "",
              optOutTaxation: (getFieldValue(row, ["Whether deductee opting out of taxation regime u/s 202(1)", "Opt Out u/s 202(1)", "Opt Out Taxation", "Opt Out"]) || 'N').toUpperCase().startsWith('Y') ? 'Y' : 'N',
              grossedUp393_10: (getFieldValue(row, ["Whether the amount paid or credited is grossed up as per section 393(10)", "Grossed Up u/s 393(10)", "Grossed Up 393(10)", "Grossed Up"]) || 'N').toUpperCase().startsWith('Y') ? 'Y' : 'N',
              residentialStatus: (getFieldValue(row, ["Res. Status", "Residential Status", "Res Status", "Resident Status"]) || 'N').toUpperCase().startsWith('Y') ? 'Y' : 'N',
              hasPE: (getFieldValue(row, ["Has PE in India", "Has PE", "PE in India", "PE"]) || 'N').toUpperCase().startsWith('Y') ? 'Y' : 'N',
              optOutRegime: (getFieldValue(row, ["Opting Out New Regime", "Opt Out New Regime", "Opting Out Regime", "Opt Out Regime"]) || 'N').toUpperCase().startsWith('Y') ? 'Y' : 'N'
            }
          })
        }

        // Parse Salary Details (optional, for 24Q Q4)
        let newSalaryDetails = []
        if (wsSalary) {
          const salaryRows = XLSX.utils.sheet_to_json(wsSalary, { defval: '' })
          newSalaryDetails = salaryRows.map(row => ({
            id: Date.now() + Math.random(),
            employeePan: row["PAN"],
            employeeName: row["Name"],
            category: row["Category"] || 'O',
            periodFrom: parseExcelDate(row["Period From"]),
            periodTo: parseExcelDate(row["Period To"]),
            salary17_1: row["Sec 17(1)"] || 0,
            valPerq17_2: row["Value Perq 17(2)"] || 0,
            profits17_3: row["Profits 17(3)"] || 0,
            grossSalary: row["Gross Salary"] || 0,
            hra: row["HRA 10(13A)"] || 0,
            travelConcession: row["LTA 10(5)"] || 0,
            otherExemptions: row["Other Exemp"] || 0,
            totalExemptions10: row["Total Exemp u/s 10"] || 0,
            deductions16: row["Ded u/s 16"] || 0,
            incomeChargeable: row["Inc Chargeable"] || 0,
            incomeOtherSources: row["Other Inc"] || 0,
            grossTotalIncome: row["Gross Tot Inc"] || 0,
            agg80C: row["Agg 80C"] || 0,
            chapterVIA: row["Chapter VI-A"] || 0,
            totalTaxableIncome: row["Taxable Inc"] || 0,
            incomeTax: row["Income Tax"] || 0,
            rebate87A: row["Rebate 87A"] || 0,
            surcharge: row["Surcharge"] || 0,
            cess: row["Cess"] || 0,
            relief89: row["Relief 89"] || 0,
            netTaxPayable: row["Net Tax"] || 0,
            totalTdsDeducted: row["Total TDS"] || 0,
            shortfallExcess: row["Shortfall/Excess"] || 0,
            regime115BAC: (row["115BAC"] && (row["115BAC"].toString().toLowerCase().startsWith('y') || row["115BAC"].toString().toLowerCase() === 'yes')) ? 'Y' : 'N',
            taxableAmtPrevEmp: row["Prev Emp Taxable"] || 0,
            tdsPrevEmp: row["Prev Emp TDS"] || 0,
            landlordPan1: row["Landlord PAN"] || '',
            landlordName1: row["Landlord Name"] || ''
          }))
        }

        // VALIDATION LOGIC
        const errors = []
        if (newChallans.length > 0) {
          newChallans.forEach((challan, index) => {
            const rowNum = index + 2

            // 1. BSR Code (7 digits) if provided
            if (challan.bsrCode && !/^\d{7}$/.test(challan.bsrCode)) {
              errors.push(`Row ${rowNum} (Challan): BSR Code must be exactly 7 digits. Found '${challan.bsrCode}'`)
            }

            // 2. Challan No (5 digits) if provided
            if (challan.challanSerialNo && !/^\d{5}$/.test(challan.challanSerialNo)) {
              errors.push(`Row ${rowNum} (Challan): Challan No must be exactly 5 digits. Found '${challan.challanSerialNo}'`)
            }

            // 3. Section Code Validation if provided
            if (challan.sectionCode && !validSectionCodes.includes(challan.sectionCode)) {
              errors.push(`Row ${rowNum} (Challan): Invalid Section Code '${challan.sectionCode}'. Valid codes are: ${validSectionCodes.join(', ')}`)
            }

            // 4. Negative Balance Check
            const total = (
              parseFloat(challan.tdsAmount || 0) +
              parseFloat(challan.surcharge || 0) +
              parseFloat(challan.educationCess || 0) +
              parseFloat(challan.higherEducationCess || 0) +
              parseFloat(challan.interest || 0) +
              parseFloat(challan.other || 0) +
              parseFloat(challan.fee || 0)
            )

            const used = newDeductees
              .filter(d => d.challanId === challan.id)
              .reduce((sum, d) => sum + parseFloat(d.tdsAmount || 0), 0)

            const balance = total - used

            if (balance < 0) {
              errors.push(`Row ${rowNum} (Challan): Balance cannot be negative. Total: ${total}, Used: ${used}.`)
            }
          })
        }

        // Validate Deductee Sections
        newDeductees.forEach((deductee, index) => {
          const rowNum = index + 2
          if (deductee.sectionCode && !validSectionCodes.includes(deductee.sectionCode)) {
            errors.push(`Row ${rowNum} (Deductee): Invalid Section Code '${deductee.sectionCode}'.`)
          }
        })

        if (errors.length > 0) {
          alert("Import Validation Failed:\n" + errors.slice(0, 10).join("\n") + (errors.length > 10 ? `\n...and ${errors.length - 10} more.` : ""))
          e.target.value = '' // Reset input
          return
        }

        // Fix mapping naming for feesAmount consistency if needed
        const finalChallans = newChallans.map(c => ({
          ...c,
          feesAmount: c.fee // Map 'fee' (from Excel) to 'feesAmount' (used in Table)
        }))

        window.dispatchEvent(new CustomEvent('importTdsData', {
          detail: { 
            challans: (finalChallans && finalChallans.length > 0) ? finalChallans : undefined, 
            deductees: newDeductees, 
            tds: newDeductees, 
            salaryDetails: newSalaryDetails 
          }
        }))

        if (newDeductees.length > 0 || finalChallans.length > 0) {
          toast.success(`Imported ${finalChallans.length > 0 ? finalChallans.length + ' Challans and ' : ''}${newDeductees.length} Deductees from Excel`);
        }

        // Simple feedback since SideBar can't use toast easily without prop
        console.log(`Imported ${newChallans.length} Challans, ${newDeductees.length} Deductees, ${newSalaryDetails.length} Salary Records`)

      } catch (err) {
        console.error(err)
        alert("Import Error: " + err.message)
      }
      e.target.value = ''
    }
    reader.readAsArrayBuffer(file)
  }

  const [showTemplateMenu, setShowTemplateMenu] = useState(false);

  const handleDownloadTemplate = (type) => {
    const defaultSection = (type === '27EQ' ? '206C(1)' : (type === '143' ? '1090' : (type === '140' ? '1004' : (type === '138' || type === '138_Q4' ? '1001' : (type === '144' ? '1039' : '194C')))));

    // Shared Headers
    const challanHeaders = [
      "Challan Serial No.", "BSR Code", "Date of Deposit (DD/MM/YYYY)", "Challan Challan No",
      "Total Amount", "Interest Allocation", "Fees Allocation", "TDS", "Surcharge", "Education Cess", "Interest", "Fee", "Penalty",
      "Section Code", "Minor Head"
    ];
    const challanExample = [["1", "0510301", "01/04/2026", "12345", "15000", "0", "0", "10000", "5000", "0", "0", "0", "0", defaultSection, "200"]];

    // Deductee Headers (Dynamic based on form type)
    let deducteeHeaders = [];
    let deducteeExample = [];

    if (type === '27Q' || type === '144') {
      deducteeHeaders = [
        "Challan Serial No. (Ref)",
        "Deductee Code",
        "PAN of Deductee",
        "First Name",
        "Middle Name",
        "Last Name",
        "Amount Paid",
        "Date of Payment (DD/MM/YYYY)",
        "Nature of Remittance",
        "Country",
        "Applicable TDS Section",
        "Opting Out New Regime",
        "Grossed Up u/s 393(10)",
        "Section Code",
        "Rate %",
        "TDS Amount",
        "Date of Deduction (DD/MM/YYYY)",
        "Certificate No. 197",
        "Reason for non-deduction"
      ];
      deducteeExample = [["1", "02", "ABCDE1234F", "Rahul", "", "Kumar", "50000", "01/04/2026", "", "", "", "N", "N", defaultSection, "2.00", "1000", "01/04/2026", "", ""]];
    } else if (type === '27EQ' || type === '143') {
      deducteeHeaders = [
        "Challan Serial No. (Ref)",
        "Collectee Code",
        "PAN of Collectee",
        "First Name",
        "Middle Name",
        "Last Name",
        "Total Value of Purchase",
        "Date of Collection (DD/MM/YYYY)",
        "Res. Status",
        "Has PE in India",
        "Opting Out New Regime",
        "Collection Code",
        "Rate %",
        "TCS Amount",
        "Date of Collection (DD/MM/YYYY)",
        "Certificate No.",
        "Remarks"
      ];
      deducteeExample = [["1", "02", "ABCDE1234F", "Rahul", "", "Kumar", "50000", "01/04/2026", "N", "N", "N", defaultSection, "2.00", "1000", "01/04/2026", "", ""]];
    } else if (type === '138' || type === '24Q' || type === '138_Q4' || type === '24Q_Q4') {
      // Salary TDS Forms (138, 24Q) - No Rate % column (Rate is slab-based)
      deducteeHeaders = [
        "Challan Serial No. (Ref)",
        "Deductee Code",
        "Section Code",
        "PAN of Deductee",
        "First Name",
        "Middle Name",
        "Last Name",
        "Amount Paid",
        "Date of Payment (DD/MM/YYYY)",
        "TDS Amount",
        "Date of Deduction (DD/MM/YYYY)",
        "Certificate No. 197",
        "Reason for non-deduction"
      ];
      deducteeExample = [["1", "02", defaultSection, "ABCDE1234F", "Rahul", "", "Kumar", "50000", "01/04/2026", "1000", "01/04/2026", "", ""]];
    } else {
      // Non-Salary Domestic TDS Forms (140, 26Q) - Includes Rate %
      deducteeHeaders = [
        "Challan Serial No. (Ref)",
        "Deductee Code",
        "Section Code",
        "PAN of Deductee",
        "First Name",
        "Middle Name",
        "Last Name",
        "Amount Paid",
        "Date of Payment (DD/MM/YYYY)",
        "Rate at which deducted",
        "TDS Amount",
        "Date of Deduction (DD/MM/YYYY)",
        "Certificate No. 197",
        "Reason for non-deduction"
      ];
      deducteeExample = [["1", "02", defaultSection, "ABCDE1234F", "Rahul", "", "Kumar", "50000", "01/04/2026", "2.00", "1000", "01/04/2026", "", ""]];
    }

    const wb = XLSX.utils.book_new();
    const wsChallan = XLSX.utils.aoa_to_sheet([challanHeaders, ...challanExample]);
    const wsDeductee = XLSX.utils.aoa_to_sheet([deducteeHeaders, ...deducteeExample]);

    XLSX.utils.book_append_sheet(wb, wsChallan, "Challan Details");
    XLSX.utils.book_append_sheet(wb, wsDeductee, "Deductee Details");

    // 24Q Q4 Specific - Salary Details
    if (type === '24Q_Q4' || type === '138_Q4') {
      const salaryHeaders = [
        "PAN", "Name", "Category", "Period From", "Period To", "Sec 17(1)", "Value Perq 17(2)", "Profits 17(3)", "Gross Salary",
        "HRA 10(13A)", "LTA 10(5)", "Other Exemp", "Total Exemp u/s 10", "Ded u/s 16", "Inc Chargeable",
        "Other Inc", "Gross Tot Inc", "Agg 80C", "Chapter VI-A", "Taxable Inc", "Income Tax", "Rebate 87A",
        "Surcharge", "Cess", "Relief 89", "Net Tax", "Total TDS", "Shortfall/Excess", "115BAC",
        "Prev Emp Taxable", "Prev Emp TDS", "Landlord PAN", "Landlord Name"
      ];
      const salaryExample = [
        ["ABCDE1234F", "Rahul Kumar", "G", "01/04/2023", "31/03/2024", "1200000", "0", "0", "1200000", "100000", "0", "0", "100000", "50000", "1050000", "0", "1050000", "150000", "150000", "900000", "50000", "0", "0", "2000", "0", "52000", "52000", "0", "No", "0", "0", "", ""]
      ];
      const wsSalary = XLSX.utils.aoa_to_sheet([salaryHeaders, ...salaryExample]);
      XLSX.utils.book_append_sheet(wb, wsSalary, "Salary Details");
    }

    // Instructions
    const instructionRows = [["Valid Section Codes:"], ...validSectionCodes.map(code => [code])];
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructionRows);
    XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

    XLSX.writeFile(wb, `TDS_Template_${type}.xlsx`);
    setShowTemplateMenu(false);
  };

  const handleExportExcel = () => {
    const currentChallans = challanData || [];
    const currentTds = tdsData || [];

    if (currentChallans.length === 0 && currentTds.length === 0) {
      alert("No return data available to download. Please fill or import Challan and Deductee details first.");
      return;
    }

    const formatDateForExcel = (dateStr) => {
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      }
      return dateStr;
    };

    // 1. Prepare Challans
    const challanSerialMap = {};
    const exportChallanRows = currentChallans.map((c, index) => {
      const serial = index + 1;
      if (c.id !== undefined && c.id !== null) {
        challanSerialMap[c.id] = serial;
        challanSerialMap[String(c.id)] = serial;
      }
      if (c.challanSerialNo) {
        challanSerialMap[c.challanSerialNo] = serial;
        challanSerialMap[String(c.challanSerialNo)] = serial;
      }
      if (c.challanNo) {
        challanSerialMap[c.challanNo] = serial;
        challanSerialMap[String(c.challanNo)] = serial;
      }
      return [
        serial, c.bsrCode || '', formatDateForExcel(c.dateOfDeposit), c.challanSerialNo || c.challanNo || '', c.totalAmount || '',
        c.interestAllocation || 0, c.feesAllocation || 0,
        c.tdsAmount || c.tds || '', c.surcharge || '', c.educationCess || '', c.interest || '',
        c.feesAmount || c.fee || '', c.penalty || '', c.sectionCode || '', c.minorHead || ''
      ];
    });

    const getChallanRef = (d) => {
      if (d.challanId !== undefined && d.challanId !== null && challanSerialMap[d.challanId]) return challanSerialMap[d.challanId];
      if (d.challanId !== undefined && d.challanId !== null && challanSerialMap[String(d.challanId)]) return challanSerialMap[String(d.challanId)];
      if (d.challanSerialNo && challanSerialMap[d.challanSerialNo]) return challanSerialMap[d.challanSerialNo];
      return d.challanSerialNo || d.challanId || '';
    };

    // 2. Prepare Deductees
    const path = (location.pathname || '').toUpperCase();
    const isSalaryForm = path.includes('138') || path.includes('24Q');
    const isForeignForm = path.includes('27Q') || path.includes('144');
    const isTcsForm = path.includes('27EQ') || path.includes('143');

    let dedHeaders = [];
    let exportDeducteeRows = [];

    if (isForeignForm) {
      dedHeaders = [
        "Challan Serial No. (Ref)", "Deductee Code", "PAN of Deductee", "First Name", "Middle Name", "Last Name",
        "Amount Paid", "Date of Payment (DD/MM/YYYY)", "Nature of Remittance", "Country", "Applicable TDS Section",
        "Opting Out New Regime", "Grossed Up u/s 393(10)", "Section Code", "Rate %", "TDS Amount",
        "Date of Deduction (DD/MM/YYYY)", "Certificate No. 197", "Reason for non-deduction"
      ];
      exportDeducteeRows = currentTds.map(d => [
        getChallanRef(d), d.deducteeCode || '02', d.panOfDeductee || d.pan || '', d.firstName || '', d.middleName || '', d.lastName || '',
        d.amountOfPayment ?? d.paymentAmount ?? d.amount ?? '', formatDateForExcel(d.dateOfPayment), d.natureOfRemittance || '', d.country || '', d.applicableTds || '',
        d.optOutTaxation || 'N', d.grossedUp393_10 || 'N', d.sectionCode || '', d.rateOfTax || '', d.tdsAmount ?? d.tds ?? d.tdsAmt ?? '',
        formatDateForExcel(d.dateOfDeduction), d.certificateNo197 || '', d.reasonForNonDeduction || ''
      ]);
    } else if (isTcsForm) {
      dedHeaders = [
        "Challan Serial No. (Ref)", "Collectee Code", "PAN of Collectee", "First Name", "Middle Name", "Last Name",
        "Total Value of Purchase", "Date of Collection (DD/MM/YYYY)", "Res. Status", "Has PE in India", "Opting Out New Regime",
        "Collection Code", "Rate %", "TCS Amount", "Date of Collection (DD/MM/YYYY)", "Certificate No.", "Remarks"
      ];
      exportDeducteeRows = currentTds.map(d => [
        getChallanRef(d), d.deducteeCode || '02', d.panOfDeductee || d.pan || '', d.firstName || '', d.middleName || '', d.lastName || '',
        d.amountOfPayment ?? d.paymentAmount ?? d.amount ?? '', formatDateForExcel(d.dateOfPayment), d.residentialStatus || 'N', d.hasPE || 'N', d.optOutRegime || 'N',
        d.sectionCode || '', d.rateOfTax || '', d.tdsAmount ?? d.tds ?? d.tdsAmt ?? '', formatDateForExcel(d.dateOfDeduction), d.certificateNo197 || '', d.reasonForNonDeduction || ''
      ]);
    } else if (isSalaryForm) {
      // Salary TDS Forms (138, 24Q) - No Rate % column
      dedHeaders = [
        "Challan Serial No. (Ref)", "Deductee Code", "Section Code", "PAN of Deductee", "First Name", "Middle Name", "Last Name",
        "Amount Paid", "Date of Payment (DD/MM/YYYY)", "TDS Amount",
        "Date of Deduction (DD/MM/YYYY)", "Certificate No. 197", "Reason for non-deduction"
      ];
      exportDeducteeRows = currentTds.map(d => [
        getChallanRef(d), d.deducteeCode || '02', d.sectionCode || '', d.panOfDeductee || d.pan || '', d.firstName || '', d.middleName || '', d.lastName || '',
        d.amountOfPayment ?? d.paymentAmount ?? d.amount ?? '', formatDateForExcel(d.dateOfPayment), d.tdsAmount ?? d.tds ?? d.tdsAmt ?? '',
        formatDateForExcel(d.dateOfDeduction), d.certificateNo197 || '', d.reasonForNonDeduction || ''
      ]);
    } else {
      // Non-Salary Domestic Forms (140, 26Q) - Includes Rate %
      dedHeaders = [
        "Challan Serial No. (Ref)", "Deductee Code", "Section Code", "PAN of Deductee", "First Name", "Middle Name", "Last Name",
        "Amount Paid", "Date of Payment (DD/MM/YYYY)", "Rate at which deducted", "TDS Amount",
        "Date of Deduction (DD/MM/YYYY)", "Certificate No. 197", "Reason for non-deduction"
      ];
      exportDeducteeRows = currentTds.map(d => [
        getChallanRef(d), d.deducteeCode || '02', d.sectionCode || '', d.panOfDeductee || d.pan || '', d.firstName || '', d.middleName || '', d.lastName || '',
        d.amountOfPayment ?? d.paymentAmount ?? d.amount ?? '', formatDateForExcel(d.dateOfPayment), d.rateOfTax || '', d.tdsAmount ?? d.tds ?? d.tdsAmt ?? '',
        formatDateForExcel(d.dateOfDeduction), d.certificateNo197 || '', d.reasonForNonDeduction || ''
      ]);
    }

    const chHeaders = [
      "Challan Serial No.", "BSR Code", "Date of Deposit (DD/MM/YYYY)", "Challan Challan No",
      "Total Amount", "Interest Allocation", "Fees Allocation", "TDS", "Surcharge", "Education Cess", "Interest", "Fee", "Penalty",
      "Section Code", "Minor Head"
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([chHeaders, ...exportChallanRows]), "Challan Details");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([dedHeaders, ...exportDeducteeRows]), "Deductee Details");
    XLSX.writeFile(wb, "TDS_Return_Data.xlsx");
  };

  const handleAiExtractedData = (data) => {
    console.log('AI Data Received in TdsSidebar:', data);
    // If data contains a nested 'data' property (new behavior), use it as payload
    const payload = (data && data.formType && data.data) ? data.data : data;

    // Handle both old schema and new strict schema fields
    // 1. Extract Deductees
    let extractTdsData = [];
    if (payload.tdsData && Array.isArray(payload.tdsData)) {
      extractTdsData = payload.tdsData;
    } else if (payload.deducteeDetails && Array.isArray(payload.deducteeDetails)) {
      extractTdsData = payload.deducteeDetails;
    } else if (Array.isArray(payload)) {
      // Fallback: if payload itself is an array, it might be the deductees
      extractTdsData = payload;
    }

    // 2. Extract Challans
    let extractChallanData = [];
    if (payload.challanData && Array.isArray(payload.challanData)) {
      extractChallanData = payload.challanData;
    }

    // 3. Extract Invoices (for GST if needed, though forms handle it directly)
    let extractInvoiceData = [];
    if (payload.invoices && Array.isArray(payload.invoices)) {
      extractInvoiceData = payload.invoices;
    } else if (payload.data && Array.isArray(payload.data)) {
      extractInvoiceData = payload.data;
    }

    const newDeductees = extractTdsData.map(d => ({
      ...d,
      id: Date.now() + Math.random(),
      // Ensure field names match what the forms expect
      panOfDeductee: d.pan || d.panOfDeductee,
      deducteeName: d.name || d.nameOfDeductee || d.deducteeName,
      amountOfPayment: d.amountPaid || d.amountOfPayment,
    }));

    const newChallans = extractChallanData.map(c => ({
      ...c,
      id: Date.now() + Math.random(),
      feesAmount: c.fee || c.feesAmount,
    }));

    const newInvoices = extractInvoiceData.map(inv => ({
      ...inv,
      id: Date.now() + Math.random(),
    }));

    console.log('Processed Data for Import:', { 
      deductees: newDeductees.length, 
      challans: newChallans.length, 
      invoices: newInvoices.length 
    });

    // Dispatch event to parent forms
    window.dispatchEvent(new CustomEvent('importTdsData', {
      detail: {
        challans: newChallans, 
        deductees: newDeductees,
        salaryDetails: data.salaryDetails || [],
        invoices: newInvoices
      }
    }));

    if (newDeductees.length > 0 || newChallans.length > 0 || newInvoices.length > 0) {
      toast.success(`Imported ${newChallans.length} Challans and ${newDeductees.length} Deductees from AI`);
    } else {
      console.warn('No structured TDS data found in payload', payload);
    }
  };

  return (
    <>
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ width: isExpanded ? expandedWidth : collapsedWidth, opacity: 1 }}
            animate={{
              width: isExpanded ? expandedWidth : collapsedWidth,
              opacity: 1
            }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={`fixed left-0 top-0 h-full bg-white/70 backdrop-blur-2xl border-r border-black/5 shadow-xl z-40 ${isMobile ? 'z-50' : 'z-40'
              }`}
            onMouseEnter={() => !isMobile && setHovered(true)}
            onMouseLeave={() => !isMobile && setHovered(false)}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex flex-col p-4 border-b border-black/5">
                <div className="flex items-center justify-between mb-4">
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center"
                    >
                      <FileText className="w-8 h-8 text-slate-900 mr-3" strokeWidth={2.5} />
                      <span className="font-bold text-slate-900 tracking-wide text-2xl">TDS Return</span>
                    </motion.div>
                  )}
                  {isMobile && (
                    <button
                      onClick={onClose}
                      className="p-1 rounded hover:bg-black/5 transition-colors"
                    >
                      <X className="w-5 h-5 text-slate-700" />
                    </button>
                  )}
                </div>

                {/* Active Client Section */}
                {isExpanded && activeClient && (
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Active Client</div>
                    <div className="text-sm font-semibold text-slate-800 truncate">{activeClient.name}</div>
                    <button 
                      onClick={() => navigate('/clients')}
                      className="mt-2 text-xs flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <ArrowLeft className="w-3 h-3 mr-1" /> Switch Client
                    </button>
                  </div>
                )}
              </div>

              {/* Collapse/Expand Button */}
              {!isMobile && (
                <button
                  onClick={() => setKeepOpen(!keepOpen)}
                  className="absolute top-1/2 -right-4 transform -translate-y-1/2 p-2 bg-white/80 backdrop-blur-md border border-black/10 rounded-full shadow-sm hover:bg-white transition-all z-50 group"
                  style={{ width: '36px', height: '36px' }}
                >
                  {keepOpen ? (
                    <ChevronLeft className="w-5 h-5 text-slate-700 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-700 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  )}
                </button>
              )}

              {/* Menu Items */}
              <div className="flex-1 overflow-y-auto py-4">
                {menuItems.map((item) => {
                  const isLocked = !selectionComplete && (item.section === 'challan-details' || item.section === 'tds-details' || item.section === 'salary-details' || item.section === 'senior-citizens-194p')
                  return (
                    <div key={item.pageKey || item.name} className="mb-2 px-2">
                      <div className="relative group">
                        <button
                          onClick={() => !isLocked && handleItemClick(item)}
                          disabled={isLocked}
                          className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                            isLocked
                              ? 'text-slate-300 cursor-not-allowed opacity-60'
                              : (item.link && isActive(item)) || (item.section && isSectionActive(item.section))
                                ? 'bg-black/10 text-slate-900 font-bold'
                                : 'text-slate-700 font-medium hover:bg-black/5 hover:text-slate-900'
                          }`}
                        >
                          <ChevronRight className={`w-6 h-6 flex-shrink-0 transition-colors ${
                            isLocked
                              ? 'text-slate-300'
                              : (item.link && isActive(item)) || (item.section && isSectionActive(item.section))
                                ? 'text-slate-900'
                                : 'text-slate-700'
                          }`} strokeWidth={2} />
                          {isExpanded && (
                            <motion.span
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -10 }}
                              className="ml-3 text-lg font-semibold flex-1 text-left"
                            >
                              {item.name}
                            </motion.span>
                          )}
                          {isLocked && isExpanded && (
                            <svg className="w-4 h-4 text-slate-300 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          )}
                        </button>
                        {/* Tooltip shown on hover when locked */}
                        {isLocked && isExpanded && (
                          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl">
                              Select FY, Quarter &amp; Return Type first
                              <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}

                {/* Generated Files Button - Separated */}
                <div className="mb-2 px-2 mt-auto border-t border-gray-100 pt-4">
                  <button
                    onClick={() => {
                      if (onSectionChange) onSectionChange('generated-files');
                      if (isMobile) onClose()
                    }}
                    className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700 ${activeSection === 'generated-files' ? 'font-bold bg-blue-50' : 'font-medium'}`}
                  >
                    <FileText className="w-6 h-6 flex-shrink-0 text-blue-600" strokeWidth={2} />
                    {isExpanded && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="ml-3 text-lg font-semibold text-blue-600"
                      >
                        Generated Files
                      </motion.span>
                    )}
                  </button>
                </div>
              </div>

              {/* Excel Actions - Middle Section */}
              {isExpanded && (
                <div className="p-4 border-t border-b border-black/5 bg-white/40 mb-24 backdrop-blur-sm">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleExcelImport}
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileInputRef.current.click()}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 mb-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Import Excel</span>
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                      title="Download Excel Template"
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 mb-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Excel Template</span>
                    </button>
                    {showTemplateMenu && (
                      <div className="absolute bottom-full mb-2 left-0 w-full bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
                        <button onClick={() => handleDownloadTemplate('24Q')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 24Q</button>
                        <button onClick={() => handleDownloadTemplate('24Q_Q4')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm font-semibold text-blue-600">Form 24Q Q4 (Salary)</button>
                        <button onClick={() => handleDownloadTemplate('138')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 138 (New 24Q)</button>
                        <button onClick={() => handleDownloadTemplate('138_Q4')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm font-semibold text-blue-600">Form 138 Q4 (Salary)</button>
                        <button onClick={() => handleDownloadTemplate('26Q')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 26Q</button>
                        <button onClick={() => handleDownloadTemplate('140')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 140 (New 26Q)</button>
                        <button onClick={() => handleDownloadTemplate('27Q')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 27Q</button>
                        <button onClick={() => handleDownloadTemplate('144')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 144 (New 27Q)</button>
                        <button onClick={() => handleDownloadTemplate('27EQ')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 27EQ</button>
                        <button onClick={() => handleDownloadTemplate('143')} className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">Form 143 (New 27EQ)</button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleExportExcel}
                    title="Download Filled Data"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Excel</span>
                  </button>
                </div>
              )}

              {/* Footer */}
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-4 border-t border-black/5"
                >

                  {activeSection !== 'generated-files' && (
                    <>
                      <button
                        onClick={() => {
                          // Trigger prepare return functionality
                          const event = new CustomEvent('prepareReturn')
                          window.dispatchEvent(event)
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Prepare TXT File</span>
                      </button>
                      <button
                        onClick={() => {
                          // Trigger headless validation functionality
                          // Triggers the same logic but explicitly labeled for the user
                          const event = new CustomEvent('validateTdsFile')
                          window.dispatchEvent(event)
                        }}
                        title="Generate FVU file"
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 mt-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Generate FVU file</span>
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => {
                      localStorage.clear()
                      sessionStorage.clear()
                      navigate('/')
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 mt-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Log Out</span>
                  </button>

                </motion.div>
              )}
            </div>
          </motion.div >
        )
        }
      </AnimatePresence>
    </>
  )
}

export default TdsSidebar;
